/**
 * ImageBubble — per-type subcomponent for `ChatMessageType.image` messages.
 *
 * Flutter source of truth:
 *  - Own variant: `currentUserMessageTypeImage` (`chat_screen.dart:1566-1661`).
 *  - Peer variant: `peerUserMessageTypeImage` (`chat_screen.dart:2667-2757`).
 *
 * Both Flutter variants are structurally identical with `VideoBubble` —
 * the only difference is the centered play-icon overlay on the video. Per
 * Task 27's plan directive ("extract the shared part into a tiny
 * `MediaBubbleFrame` if >50% of the layout is shared"), the entire bubble
 * shell — sizing, corner radii, placeholder, padding, tap target — lives in
 * `MediaBubbleFrame`. This component just resolves the thumbnail URL,
 * stubs the tap, and delegates rendering.
 *
 * Tap routing (Task 15 / consideration J): a READY image bubble taps to the
 * directly-routable `MediaDetailsPage` (`/chat/media/:id`) via
 * `Navigator.navigateToMediaDetails`, passing the message + the peer's public
 * JWK as route args (the page decrypts client-side; a pasted URL has no args).
 * A bubble that is still uploading (`isUploading`) or failed (`success ===
 * false`) shows an `InfoSheet` instead of opening the page — mirroring
 * `UserDetailsPage.handleMediaClick`'s processing gate.
 *
 * Thumbnail path: the encrypted thumbnail bytes are fetched+decrypted by
 * `EncryptedCachedImage`, which now takes the BUCKET-RELATIVE object path and
 * resolves a valid, tokenized Firebase Storage download URL itself via the
 * domain UseCase (Finding 5 — the old `DEFAULT_BUCKET_PREFIX + relativePath`
 * concatenation produced an un-encoded, token-less URL the Storage REST
 * endpoint rejected with HTTP 400). So this component hands the frame the raw
 * `message.relativeThumbnailPath` and does NOT prefix the bucket. The Flutter
 * chat-screen code wraps the thumbnail in a `ContentThumbnailPagerWidget`
 * against a synthetic `UserMedia` with `isContent = false`
 * (`chat_screen.dart:1581-1593`); the URL build is web-read-path-specific (this
 * encrypted sub-folder surface was never fetched from web in Flutter).
 */

import React from 'react';
import { ChatMessageType } from '@/domain/enums/chatMessageType';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { Navigator } from '@/presentation/routing/Navigator';
import {
  registerBubble,
  type BubbleSubcomponentProps,
} from '../../registry/bubbleRegistry';
import { MediaBubbleFrame } from './MediaBubbleFrame';

export const ImageBubble: React.FC<BubbleSubcomponentProps> = ({
  message,
  peerUser,
  isOwn,
  isLastOfRun,
  onRetry,
  screenKey,
}) => {
  const { l10n } = useLocale();

  // Bucket-RELATIVE thumbnail object path. `EncryptedCachedImage` (via the
  // encrypted branch of `MediaBubbleFrame`) resolves the valid, tokenized
  // Firebase Storage download URL from this relative path through the domain
  // UseCase before fetching (Finding 5) — we must NOT prefix
  // `DEFAULT_BUCKET_PREFIX` here, which yielded an un-encoded, token-less URL
  // the Storage REST endpoint rejected with HTTP 400.
  const thumbnailPath = message.relativeThumbnailPath ?? '';

  // Defensive uploading branch — Flutter renders a gray placeholder while
  // an image is being uploaded from this client. Round one has no client
  // upload path, but a peer might be uploading from mobile.
  const isUploading = message.isUploading === true;

  // Tap handler (consideration J — mirror `UserDetailsPage.handleMediaClick`):
  //  1. Own + failed → re-upload via `onRetry` (Finding 3, unchanged — mirrors
  //     `TextBubble`'s `onRetry` branch, `chat_screen.dart:1400-1403`).
  //  2. Still uploading (`isUploading`) → processing InfoSheet, no navigation.
  //  3. Failed (`success === false`) and NOT own → failed InfoSheet (an own
  //     failure is already handled by branch 1).
  //  4. Otherwise READY → open `MediaDetailsPage` with the message + peer JWK as
  //     route args (the page decrypts client-side; a pasted URL has no args).
  const handleTap = (): void => {
    if (isOwn && message.success === false) {
      onRetry(message);
      return;
    }
    if (isUploading) {
      void InfoSheet.show({
        title: l10n.chat.mediaDetails.processingTitle,
        infoText: l10n.chat.mediaDetails.processingDescription,
        screenKey,
        qaId: 'media-info-sheet',
      });
      return;
    }
    if (message.success === false) {
      void InfoSheet.show({
        title: l10n.chat.mediaDetails.failedTitle,
        infoText: l10n.chat.mediaDetails.failedDescription,
        screenKey,
        qaId: 'media-info-sheet',
      });
      return;
    }
    Navigator.navigateToMediaDetails(message.documentId, {
      message,
      // Same key the chat already derived for the open conversation's peer
      // (`useChat.runInitialLoad`); the page re-derives it to decrypt.
      peerPublicJwk: peerUser?.publicJwk ?? null,
    });
  };

  return (
    <MediaBubbleFrame
      isOwn={isOwn}
      isLastOfRun={isLastOfRun}
      aspectRatio={message.aspectRatio}
      thumbnailSource={thumbnailPath}
      // The thumbnail bytes are E2EE ciphertext; the peer's public JWK drives
      // the chat-key decrypt inside `EncryptedCachedImage`. The chat already
      // derived this key for the open conversation's peer at load time
      // (`useChat.runInitialLoad`); threading it here keeps the per-tile
      // decrypt self-healing for the single open-chat peer.
      peerPublicJwk={peerUser?.publicJwk ?? null}
      isUploading={isUploading}
      processingLabel={l10n.chat.uploading.image}
      onTap={handleTap}
      qa={{ id: `chat-bubble-image-${message.documentId}` }}
    />
  );
};

// Register with the dispatcher so `MessageBubble` routes
// `ChatMessageType.image` to this component. See `MessageBubble.tsx` for
// the registry contract.
registerBubble(ChatMessageType.image, ImageBubble);
